import AjaxService from '../../services/ajax-service.js';

/**
 * Simple Departure Filter Class
 *
 * Handles filtering functionality for departure dates.
 */
export default class DepartureFilter {
	constructor(container, options = {}) {
		this.container = container;
		this.filterType = options.filterType || 'tabbed';
		this.isHome = options.isHome || false;
		this.productId = options.productId || 0;
		this.limit = options.limit || 10; // Store the limit from options
		this.loadMoreEnabled = options.loadMoreEnabled || false; // Store the load more enabled option

		this.ajaxService = new AjaxService();
		this.currentMonth = this.getCurrentMonth();

		this.init();
	}

	/**
	 * Get current month from active tab/dropdown
	 */
	getCurrentMonth() {
		if (this.filterType === 'tabbed') {
			const activeTab = this.container.querySelector(
				'.departure-filter__tab--active'
			);
			if (activeTab) {
				return activeTab.dataset.tab;
			}
		} else if (this.filterType === 'dropdown') {
			const dropdown = this.container.querySelector(
				'.departure-filter__dropdown'
			);
			if (dropdown && dropdown.value) {
				return dropdown.value;
			}
		}
		return null;
	}

	/**
	 * Initialize the filter
	 */
	init() {
		if (this.filterType === 'tabbed') {
			this.bindTabEvents();
		} else if (this.filterType === 'dropdown') {
			this.bindDropdownEvents();
		}

		// Initialize load more functionality for page load only when enabled
		if (this.loadMoreEnabled) {
			this.initializeLoadMoreOnPageLoad();
			// Bind load more buttons that are already in the DOM (from initial page load)
			this.bindInitialLoadMoreButtons();
		}
	}

	/**
	 * Bind tab events
	 */
	bindTabEvents() {
		const tabs = this.container.querySelectorAll(
			'.departure-filter__tab'
		);
		tabs.forEach((tab) => {
			tab.addEventListener('click', (e) => this.handleTabClick(e));
		});
	}

	/**
	 * Bind dropdown events
	 */
	bindDropdownEvents() {
		const dropdown = this.container.querySelector(
			'.departure-filter__dropdown'
		);
		if (dropdown) {
			dropdown.addEventListener('change', (e) =>
				this.handleDropdownChange(e)
			);
		}
	}

	/**
	 * Bind event listeners to initial load more buttons
	 * Now works with the global load more button approach
	 */
	bindInitialLoadMoreButtons() {
		// Find the global load more button
		const globalLoadMoreBtn = this.container.querySelector(
			'.departure-filter__global-load-more .departure-filter__load-more-btn'
		);

		if (globalLoadMoreBtn) {
			// Remove any existing event listeners to prevent duplicates
			const newButton = globalLoadMoreBtn.cloneNode(true);
			globalLoadMoreBtn.parentNode.replaceChild(
				newButton,
				globalLoadMoreBtn
			);

			// Add the event listener to the new button
			newButton.addEventListener('click', (e) =>
				this.handleGlobalLoadMore(e)
			);
		}
	}

	/**
	 * Handle tab click
	 */
	async handleTabClick(event) {
		event.preventDefault();

		const tab = event.currentTarget;
		const monthYear = tab.dataset.tab;

		if (!monthYear || monthYear === this.currentMonth) return;

		// Check if content already exists (pre-rendered)
		const existingContent = this.container.querySelector(
			`#${monthYear}`
		);

		if (existingContent) {
			// Use immediate switching for pre-rendered content
			this.switchToMonth(monthYear);
			this.updateActiveTab(tab);
		} else {
			// Use AJAX for dynamic content
			await this.loadContentViaAjax(monthYear);
			this.updateActiveTab(tab);
		}
	}

	/**
	 * Handle dropdown change
	 */
	async handleDropdownChange(event) {
		const monthYear = event.target.value;

		if (!monthYear) {
			// Load all content with pagination
			await this.loadAllContent();
			this.currentMonth = null;
			return;
		}

		if (monthYear === this.currentMonth) return;

		// Check if content already exists (pre-rendered)
		const existingContent = this.container.querySelector(
			`[data-month="${monthYear}"]`
		);

		if (existingContent) {
			// Use immediate switching for pre-rendered content
			this.switchToMonthDropdown(monthYear);
		} else {
			// Use AJAX for dynamic content
			await this.loadContentViaAjaxDropdown(monthYear);
		}
	}

	/**
	 * Remove existing content for filters OTHER than the target filter
	 * This prevents duplicates while preserving the target filter's content
	 */
	removeOtherFiltersContent(targetMonthYear) {
		const contentContainer = this.container.querySelector(
			'.departure-filter__content'
		);
		if (contentContainer) {
			// Remove content for other filters, but preserve the target filter
			const allContent = contentContainer.querySelectorAll(
				'.departure-filter__dropdown-content, .departure-filter__tab-content, .departure-filter__all-content'
			);

			allContent.forEach((content) => {
				let contentMonth = '';

				// Determine which month this content belongs to
				if (
					content.classList.contains(
						'departure-filter__all-content'
					)
				) {
					contentMonth = 'all';
				} else if (content.id && content.id.startsWith('tab-')) {
					contentMonth = content.id;
				} else if (content.hasAttribute('data-month')) {
					contentMonth = content.getAttribute('data-month');
				}

				// Remove content if it's NOT the target filter
				if (contentMonth !== targetMonthYear) {
					content.remove();
				}
			});
		}
	}

	/**
	 * Reset load more state for ALL filters
	 * This ensures that no filter retains its previous state
	 */
	resetAllFilters() {
		// Reset all tab content
		const allTabContent = this.container.querySelectorAll(
			'.departure-filter__tab-content'
		);
		allTabContent.forEach((content) => {
			const monthYear = content.id;
			if (monthYear) {
				this.resetLoadMoreState(monthYear);
			}
		});

		// Reset all dropdown content
		const allDropdownContent = this.container.querySelectorAll(
			'.departure-filter__dropdown-content'
		);
		allDropdownContent.forEach((content) => {
			const monthYear = content.getAttribute('data-month');
			if (monthYear) {
				this.resetLoadMoreState(monthYear);
			}
		});

		// Reset all content
		const allContent = this.container.querySelector(
			'.departure-filter__all-content'
		);
		if (allContent) {
			this.resetLoadMoreState('all');
		}
	}

	/**
	 * Reset load more state for a specific month/year
	 * This ensures that when switching back to a filter, only the initial rows are visible
	 */
	resetLoadMoreState(monthYear) {
		const monthYearClean = monthYear.includes('tab-')
			? monthYear.replace('tab-', '')
			: monthYear;
		const contentSelector =
			monthYear === 'all'
				? '.departure-filter__all-content'
				: monthYear.includes('tab-')
					? `#${monthYear}`
					: `[data-month="${monthYearClean}"]`;

		const content = this.container.querySelector(contentSelector);
		if (content) {
			// Only apply limits and hidden classes when load more is enabled
			if (this.loadMoreEnabled) {
				// Hide all rows beyond the initial limit
				const rows = content.querySelectorAll(
					'.departure-filter__item-row'
				);
				let hiddenCount = 0;
				let visibleCount = 0;

				rows.forEach((row, index) => {
					if (index >= this.limit) {
						row.classList.add('departure-filter__item-row--hidden');
						hiddenCount++;
					} else {
						row.classList.remove(
							'departure-filter__item-row--hidden'
						);
						visibleCount++;
					}
				});

				// Reset load more button state - find ALL buttons for this month to ensure we reset the right one
				const loadMoreBtns = content.querySelectorAll(
					'.departure-filter__load-more-btn'
				);

				loadMoreBtns.forEach((btn, index) => {
					const oldVisible = btn.getAttribute('data-visible');
					const btnMonth = btn.getAttribute('data-month');
					const btnType = btn.getAttribute('data-type');

					// Reset the visible count to the initial limit
					btn.setAttribute('data-visible', this.limit);
					btn.style.display = 'inline-block'; // Show the button again
				});
			} else {
				// When load more is disabled, show all rows
				const rows = content.querySelectorAll(
					'.departure-filter__item-row'
				);
				rows.forEach((row) => {
					row.classList.remove('departure-filter__item-row--hidden');
				});
			}
		}
	}

	/**
	 * Switch to specific month (for tabbed layout)
	 */
	switchToMonth(monthYear) {
		// Show container loader
		this.showContainerLoader();

		// Remove content for other filters, but preserve the target filter
		this.removeOtherFiltersContent(monthYear);

		// Reset the target filter to ensure proper state
		this.resetLoadMoreState(monthYear);

		// Hide all tab content
		const allContent = this.container.querySelectorAll(
			'.departure-filter__tab-content'
		);
		allContent.forEach((content) => {
			content.classList.remove('departure-filter__tab-content--active');
		});

		// Show the selected tab content
		const selectedContent = this.container.querySelector(
			`#${monthYear}`
		);
		if (selectedContent) {
			selectedContent.classList.add(
				'departure-filter__tab-content--active'
			);
		}

		this.currentMonth = monthYear;

		// Create global load more button for the new filter
		this.createGlobalLoadMoreButton();

		// Hide container loader after a short delay to ensure smooth transition
		setTimeout(() => {
			this.hideContainerLoader();
		}, 300);
	}

	/**
	 * Switch to specific month (for dropdown layout)
	 */
	switchToMonthDropdown(monthYear) {
		// Show container loader
		this.showContainerLoader();

		// Remove content for other filters, but preserve the target filter
		this.removeOtherFiltersContent(monthYear);

		// Reset the target filter to ensure proper state
		this.resetLoadMoreState(monthYear);

		// Hide all dropdown content
		const allContent = this.container.querySelectorAll(
			'.departure-filter__dropdown-content'
		);
		allContent.forEach((content) => {
			content.classList.remove(
				'departure-filter__dropdown-content--active'
			);
		});

		// Show the selected dropdown content
		const selectedContent = this.container.querySelector(
			`[data-month="${monthYear}"]`
		);
		if (selectedContent) {
			selectedContent.classList.add(
				'departure-filter__dropdown-content--active'
			);
		}

		this.currentMonth = monthYear;

		// Create global load more button for the new filter
		this.createGlobalLoadMoreButton();

		// Hide container loader after a short delay to ensure smooth transition
		setTimeout(() => {
			this.hideContainerLoader();
		}, 300);
	}

	/**
	 * Load content via AJAX for tabbed layout
	 */
	async loadContentViaAjax(monthYear) {
		try {
			// Extract month-year from tab ID (e.g., "tab-2025-11" -> "2025-11")
			const monthYearClean = monthYear.replace('tab-', '');

			const response = await this.ajaxService.loadFilteredContent({
				month_year: monthYearClean,
				product_id: this.productId,
				is_home: this.isHome,
			});

			if (response.success) {
				this.createTabContent(monthYear, response.data);
				this.switchToMonth(monthYear);
				this.currentMonth = monthYear;
			} else {
				this.showError('Failed to load content');
			}
		} catch (error) {
			this.showError('Failed to load content');
		}
	}

	/**
	 * Load content via AJAX for dropdown layout
	 */
	async loadContentViaAjaxDropdown(monthYear) {
		try {
			// Ensure monthYear is in correct format (e.g., "2025-11")
			const monthYearClean = monthYear.includes('tab-')
				? monthYear.replace('tab-', '')
				: monthYear;

			const response = await this.ajaxService.loadFilteredContent({
				month_year: monthYearClean,
				product_id: this.productId,
				is_home: this.isHome,
			});

			if (response.success) {
				this.createDropdownContent(monthYear, response.data);
				this.switchToMonthDropdown(monthYear);
				this.currentMonth = monthYear;
			} else {
				this.showError('Failed to load content');
			}
		} catch (error) {
			this.showError('Failed to load content');
		}
	}

	/**
	 * Create new tab content dynamically
	 */
	createTabContent(monthYear, data) {
		const contentContainer = this.container.querySelector(
			'.departure-filter__content'
		);
		if (contentContainer) {
			// Remove existing content for this month
			const existingContent = contentContainer.querySelector(
				`#${monthYear}`
			);
			if (existingContent) {
				existingContent.remove();
			}

			// Create new tab content
			const tabContent = document.createElement('div');
			tabContent.id = monthYear;
			tabContent.className = 'departure-filter__tab-content';
			tabContent.innerHTML = data.html;

			// Note: Global load more button is created separately by createGlobalLoadMoreButton()

			contentContainer.appendChild(tabContent);

			// Rebind any necessary event listeners
			this.bindInitialLoadMoreButtons();
		}
	}

	/**
	 * Create new dropdown content dynamically
	 */
	createDropdownContent(monthYear, data) {
		const contentContainer = this.container.querySelector(
			'.departure-filter__content'
		);
		if (contentContainer) {
			// Remove existing content for this month
			const existingContent = contentContainer.querySelector(
				`[data-month="${monthYear}"]`
			);
			if (existingContent) {
				existingContent.remove();
			}

			// Create new dropdown content
			const dropdownContent = document.createElement('div');
			dropdownContent.className = 'departure-filter__dropdown-content';
			dropdownContent.setAttribute('data-month', monthYear);
			dropdownContent.innerHTML = data.html;

			// Note: Global load more button is created separately by createGlobalLoadMoreButton()

			contentContainer.appendChild(dropdownContent);

			// Rebind any necessary event listeners
			this.bindInitialLoadMoreButtons();
		}
	}

	/**
	 * Show all content
	 */
	showAllContent() {
		const contentElements = this.container.querySelectorAll(
			'.departure-filter__dropdown-content'
		);
		contentElements.forEach((element) => {
			element.classList.add(
				'departure-filter__dropdown-content--active'
			);
		});
	}

	/**
	 * Load all content with pagination
	 */
	async loadAllContent() {
		try {
			// Reset load more state for any previously loaded content
			if (this.currentMonth && this.currentMonth !== 'all') {
				this.resetLoadMoreState(this.currentMonth);
			}

			const response = await this.ajaxService.loadFilteredContent({
				month_year: '', // Empty for all months
				product_id: this.productId,
				is_home: this.isHome,
			});

			if (response.success) {
				this.createAllContent(response.data);
				this.currentMonth = 'all';

				// Create global load more button for all content
				this.createGlobalLoadMoreButton();
			} else {
				this.showError('Failed to load all content');
			}
		} catch (error) {
			this.showError('Failed to load all content');
		}
	}

	/**
	 * Create all content container with pagination
	 */
	createAllContent(data) {
		const contentContainer = this.container.querySelector(
			'.departure-filter__content'
		);
		if (contentContainer) {
			// Remove existing content
			contentContainer.innerHTML = '';

			// Create all content container
			const allContent = document.createElement('div');
			allContent.className =
				'departure-filter__all-content departure-filter__dropdown-content--active';
			allContent.setAttribute('data-month', 'all');
			allContent.innerHTML = data.html;

			// Note: Global load more button is created separately by createGlobalLoadMoreButton()

			contentContainer.appendChild(allContent);

			// Rebind any necessary event listeners
			this.bindInitialLoadMoreButtons();
		}
	}

	/**
	 * Show more items incrementally based on load more count
	 */
	showMoreItems(monthYear, type, currentVisible, newVisible) {
		let content;
		if (monthYear === 'all') {
			content = this.container.querySelector(
				'.departure-filter__all-content'
			);
		} else if (type === 'tab') {
			content = this.container.querySelector(`#${monthYear}`);
		} else {
			content = this.container.querySelector(
				`[data-month="${monthYear}"]`
			);
		}

		if (content) {
			// Get all rows and filter to only hidden ones
			const allRows = content.querySelectorAll(
				'.departure-filter__item-row'
			);
			const hiddenRows = content.querySelectorAll(
				'.departure-filter__item-row--hidden'
			);

			// Calculate how many rows to reveal
			const rowsToReveal = newVisible - currentVisible;
			let revealedCount = 0;

			// Reveal only the number of rows specified by the load more count
			hiddenRows.forEach((row, index) => {
				if (index < rowsToReveal) {
					row.classList.remove(
						'departure-filter__item-row--hidden'
					);
					revealedCount++;
				}
			});
		}
	}

	/**
	 * Create load more button
	 */
	createLoadMoreButton(data, monthYear, type) {
		const loadMoreContainer = document.createElement('div');
		loadMoreContainer.className = 'departure-filter__load-more';

		const button = document.createElement('button');
		button.className = 'departure-filter__load-more-btn';
		button.textContent = 'Load More';
		button.setAttribute('data-total', data.total);
		button.setAttribute('data-visible', this.limit); // Start with the initial limit
		button.setAttribute('data-month', monthYear);
		button.setAttribute('data-type', type);

		button.addEventListener('click', (e) =>
			this.handleLoadMore(e, data)
		);

		loadMoreContainer.appendChild(button);
		return loadMoreContainer;
	}

	/**
	 * Handle load more button click
	 */
	async handleLoadMore(event, data) {
		const button = event.target;
		const monthYear = button.getAttribute('data-month');
		const type = button.getAttribute('data-type');
		const total = parseInt(button.getAttribute('data-total'));
		const currentlyVisible = parseInt(
			button.getAttribute('data-visible')
		);

		try {
			button.disabled = true;
			button.classList.add('loading');

			// Get the load more count from admin settings (default to 5)
			const loadMoreCount = this.getLoadMoreCount();
			const newVisibleCount = currentlyVisible + loadMoreCount;

			// Show more items
			this.showMoreItems(
				monthYear,
				type,
				currentlyVisible,
				newVisibleCount
			);

			// Update button attributes
			button.setAttribute('data-visible', newVisibleCount);

			// Hide button if all items are now visible
			if (newVisibleCount >= total) {
				button.style.display = 'none';
			}
		} catch (error) {
			this.showError('Failed to load more content');
		} finally {
			button.disabled = false;
			button.classList.remove('loading');
		}
	}

	/**
	 * Get load more count from admin settings
	 */
	getLoadMoreCount() {
		// Try to get from data attribute first, fallback to default
		const loadMoreCount = this.container.getAttribute(
			'data-load-more-count'
		);
		return loadMoreCount ? parseInt(loadMoreCount) : 5;
	}



	/**
	 * Update active tab
	 */
	updateActiveTab(activeTab) {
		const tabs = this.container.querySelectorAll(
			'.departure-filter__tab'
		);
		tabs.forEach((tab) =>
			tab.classList.remove('departure-filter__tab--active')
		);
		activeTab.classList.add('departure-filter__tab--active');
	}

	/**
	 * Show error message
	 */
	showError(message) {
		const errorDiv = document.createElement('div');
		errorDiv.className = 'departure-filter__error';
		errorDiv.textContent = message;

		const contentArea = this.container.querySelector(
			'.departure-filter__content'
		);
		if (contentArea) {
			contentArea.innerHTML = '';
			contentArea.appendChild(errorDiv);
		}
	}

	/**
	 * Destroy filter
	 */
	destroy() {
		// Remove event listeners if needed
		this.container = null;
		this.ajaxService = null;
	}

	/**
	 * Initialize load more functionality for page load
	 */
	initializeLoadMoreOnPageLoad() {
		// Only initialize load more when it's enabled
		if (!this.loadMoreEnabled) {
			return;
		}

		// Remove any existing load more buttons
		this.container.querySelectorAll('.departure-filter__load-more').forEach(
			(button) => {
				button.remove();
			}
		);

		// Create the global load more button
		this.createGlobalLoadMoreButton();
	}

	/**
	 * Get the default content when no specific filter is selected
	 */
	getDefaultContent() {
		// Check for default content that might not have active classes
		const defaultContent = this.container.querySelector(
			'.departure-filter__content > div'
		);
		if (defaultContent) {
			return defaultContent;
		}

		// Also check for any content with departure rows
		const anyContentWithRows = this.container.querySelector(
			'.departure-filter__content .departure-filter__item-row'
		);
		if (anyContentWithRows) {
			return anyContentWithRows.closest(
				'.departure-filter__dropdown-content, .departure-filter__tab-content, .departure-filter__all-content'
			);
		}

		return null;
	}

	/**
	 * Create a single, global load more button
	 */
	createGlobalLoadMoreButton() {
		// Only create load more button if load more is enabled
		if (!this.loadMoreEnabled) {
			return;
		}

		// Remove any existing global load more button
		const existingButton = this.container.querySelector(
			'.departure-filter__global-load-more'
		);
		if (existingButton) {
			existingButton.remove();
		}

		// Get the current active content to determine if we need a load more button
		const activeContent = this.getActiveContent();
		if (!activeContent) {
			return;
		}

		const totalRows = activeContent.querySelectorAll(
			'.departure-filter__item-row'
		).length;
		const visibleRows = activeContent.querySelectorAll(
			'.departure-filter__item-row:not(.departure-filter__item-row--hidden)'
		).length;

		// Only create button if there are more items than the limit
		if (totalRows > this.limit) {
			const loadMoreContainer = document.createElement('div');
			loadMoreContainer.className = 'departure-filter__global-load-more';

			const button = document.createElement('button');
			button.className = 'departure-filter__load-more-btn';
			button.textContent = 'Load More';
			button.setAttribute('data-total', totalRows);
			button.setAttribute('data-visible', this.limit);
			button.setAttribute(
				'data-current-filter',
				this.currentMonth || 'all'
			);

			button.addEventListener('click', (e) =>
				this.handleGlobalLoadMore(e)
			);

			loadMoreContainer.appendChild(button);

			// Add the button to the main container
			const contentContainer = this.container.querySelector(
				'.departure-filter__content'
			);
			if (contentContainer) {
				contentContainer.appendChild(loadMoreContainer);
			}
		}
	}

	/**
	 * Get the currently active content (visible filter)
	 */
	getActiveContent() {
		// Check for active dropdown content
		const activeDropdown = this.container.querySelector(
			'.departure-filter__dropdown-content--active'
		);
		if (activeDropdown) {
			return activeDropdown;
		}

		// Check for active tab content
		const activeTab = this.container.querySelector(
			'.departure-filter__tab-content--active'
		);
		if (activeTab) {
			return activeTab;
		}

		// Check for all content (default view)
		const allContent = this.container.querySelector(
			'.departure-filter__all-content'
		);
		if (allContent) {
			return allContent;
		}

		// Check for no-filter layout content - look for the table body directly
		const noFilterTableBody = this.container.querySelector(
			'.departure-filter--no-filter .departure-filter__table-body'
		);
		if (noFilterTableBody) {
			return noFilterTableBody;
		}

		// Check for no-filter layout content - look for the content div
		const noFilterContent = this.container.querySelector(
			'.departure-filter--no-filter .departure-filter__content'
		);
		if (noFilterContent) {
			return noFilterContent;
		}

		// If no active content found, look for any content that might be visible by default
		const anyContent = this.container.querySelector(
			'.departure-filter__content > div'
		);
		if (anyContent) {
			return anyContent;
		}

		// Last resort: look for any content with departure rows
		const anyContentWithRows = this.container.querySelector(
			'.departure-filter__content .departure-filter__item-row'
		);
		if (anyContentWithRows) {
			const parentContent = anyContentWithRows.closest(
				'.departure-filter__dropdown-content, .departure-filter__tab-content, .departure-filter__all-content, .departure-filter__content, .departure-filter__table-body'
			);
			if (parentContent) {
				return parentContent;
			}
		}

		return null;
	}

	/**
	 * Handle global load more button click
	 */
	async handleGlobalLoadMore(event) {
		const button = event.target;
		const total = parseInt(button.getAttribute('data-total'));
		const currentlyVisible = parseInt(
			button.getAttribute('data-visible')
		);
		const currentFilter = button.getAttribute('data-current-filter');

		try {
			button.disabled = true;
			button.classList.add('loading');

			// Show container loader
			this.showContainerLoader();

			// Get the load more count from admin settings
			const loadMoreCount = this.getLoadMoreCount();
			const newVisibleCount = Math.min(
				currentlyVisible + loadMoreCount,
				total
			);

			// Show more items
			this.showMoreItemsGlobal(currentlyVisible, newVisibleCount);

			// Update button attributes
			button.setAttribute('data-visible', newVisibleCount);

			// Hide button if all items are now visible
			if (newVisibleCount >= total) {
				button.style.display = 'none';
			}
		} catch (error) {
			this.showError('Failed to load more content');
		} finally {
			button.disabled = false;
			button.classList.remove('loading');

			// Hide container loader after a short delay
			setTimeout(() => {
				this.hideContainerLoader();
			}, 200);
		}
	}

	/**
	 * Show more items globally (works with any active filter)
	 */
	showMoreItemsGlobal(currentVisible, newVisible) {
		const activeContent = this.getActiveContent();
		if (!activeContent) {
			return;
		}

		const rows = activeContent.querySelectorAll(
			'.departure-filter__item-row'
		);
		const rowsToReveal = newVisible - currentVisible;
		let revealedCount = 0;

		rows.forEach((row, index) => {
			if (index >= currentVisible && index < newVisible) {
				row.classList.remove('departure-filter__item-row--hidden');
				revealedCount++;
			}
		});

		// Update the global load more button with new count
		const loadMoreBtn = this.container.querySelector(
			'.departure-filter__global-load-more .departure-filter__load-more-btn'
		);
		if (loadMoreBtn) {
			loadMoreBtn.setAttribute('data-visible', newVisible);
		}
	}

	/**
	 * Create a single global load more button for the currently active content
	 */
	createGlobalLoadMoreButton() {
		// Only create load more button if load more is enabled
		if (!this.loadMoreEnabled) {
			return;
		}

		// Remove any existing global load more button
		const existingButton = this.container.querySelector(
			'.departure-filter__global-load-more'
		);
		if (existingButton) {
			existingButton.remove();
		}

		const activeContent = this.getActiveContent();
		if (!activeContent) {
			return;
		}

		const totalRows = activeContent.querySelectorAll(
			'.departure-filter__item-row'
		).length;
		const visibleRows = activeContent.querySelectorAll(
			'.departure-filter__item-row:not(.departure-filter__item-row--hidden)'
		).length;

		if (totalRows > this.limit) {
			const loadMoreContainer = document.createElement('div');
			loadMoreContainer.className = 'departure-filter__global-load-more';

			const button = document.createElement('button');
			button.className = 'departure-filter__load-more-btn';
			button.textContent = 'Load More';
			button.setAttribute('data-total', totalRows);
			button.setAttribute('data-visible', this.limit); // Initial visible count is the limit
			button.setAttribute(
				'data-current-filter',
				this.currentMonth || 'all'
			);

			button.addEventListener('click', (e) =>
				this.handleGlobalLoadMore(e)
			);

			loadMoreContainer.appendChild(button);

			const contentContainer = this.container.querySelector(
				'.departure-filter__content'
			);
			if (contentContainer) {
				contentContainer.appendChild(loadMoreContainer);
			}
		}
	}

	/**
	 * Initialize load more functionality on page load
	 * This removes any existing per-filter buttons and creates the global button
	 */
	initializeLoadMoreOnPageLoad() {
		// Only initialize load more when it's enabled
		if (!this.loadMoreEnabled) {
			return;
		}

		// Remove any existing per-filter load more buttons (from PHP templates)
		const existingButtons = this.container.querySelectorAll(
			'.departure-filter__load-more'
		);
		existingButtons.forEach((button) => {
			button.remove();
		});

		// Create the global load more button
		this.createGlobalLoadMoreButton();
	}

	/**
	 * Show container loader
	 */
	showContainerLoader() {
		const loader = this.container.querySelector(
			'.departure-filter__container-loader'
		);
		if (loader) {
			loader.classList.add('loading');
		}
	}

	/**
	 * Hide container loader
	 */
	hideContainerLoader() {
		const loader = this.container.querySelector(
			'.departure-filter__container-loader'
		);
		if (loader) {
			loader.classList.remove('loading');
		}
	}
}
